/** Shared UI helpers for fields, copy buttons, and autosize. */

const COPY_ICON = `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;

export function fieldWithCopy(options: {
  id: string;
  label: string;
  placeholder?: string;
  rows?: number;
  readonly?: boolean;
  copy?: boolean;
}): string {
  const copyBtn = options.copy !== false
    ? `<button type="button" class="icon-btn" data-copy="#${options.id}" title="Copy to clipboard" aria-label="Copy ${options.label}">${COPY_ICON}</button>`
    : '';
  return `
    <div class="field">
      <div class="field-head">
        <label for="${options.id}">${options.label}</label>
        ${copyBtn}
      </div>
      <textarea
        id="${options.id}"
        class="auto-grow"
        spellcheck="false"
        rows="${options.rows ?? 2}"
        placeholder="${options.placeholder ?? ''}"
        ${options.readonly ? 'readonly' : ''}
      ></textarea>
    </div>`;
}

export function autosizeTextarea(el: HTMLTextAreaElement): void {
  el.style.height = 'auto';
  const next = Math.max(el.scrollHeight, 56);
  el.style.height = `${next}px`;
  el.classList.toggle('has-content', el.value.trim().length > 0);
}

export function wireAutoGrow(root: ParentNode): void {
  root.querySelectorAll<HTMLTextAreaElement>('textarea.auto-grow').forEach((ta) => {
    const sync = () => autosizeTextarea(ta);
    ta.addEventListener('input', sync);
    // Initial + after font layout
    sync();
    requestAnimationFrame(sync);
  });
}

export function wireCopyButtons(
  root: ParentNode,
  onCopied?: () => void,
): void {
  root.querySelectorAll<HTMLButtonElement>('[data-copy]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const sel = btn.getAttribute('data-copy');
      if (!sel) return;
      const target = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(sel);
      if (!target) return;
      void navigator.clipboard.writeText(target.value).then(() => {
        btn.classList.add('copied');
        window.setTimeout(() => btn.classList.remove('copied'), 900);
        onCopied?.();
      });
    });
  });
}

export function hasTextContent(...els: Array<HTMLTextAreaElement | HTMLInputElement | null>): boolean {
  return els.some((el) => !!el && el.value.trim().length > 0);
}

export function refreshAutoGrow(root: ParentNode): void {
  root.querySelectorAll<HTMLTextAreaElement>('textarea.auto-grow').forEach(autosizeTextarea);
}
